import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuDownload, LuFile, LuFileText, LuFolderOpen, LuSearchX, LuTag, LuTriangleAlert } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { useTranslation } from '../../../i18n/LanguageContext';
import { downloadFileAs } from '../../../utils/downloadFile';
import { EmptyState, LoadingRows, SearchField } from '../components/ProfileUi';
import DocumentRow, { KIND_TONES } from './DocumentRow';

const WORD_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const getKind = (docItem) => {
  const ext = (docItem.fileName || '').split('.').pop().toLowerCase();
  if (docItem.fileType === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (WORD_TYPES.includes(docItem.fileType) || ext === 'doc' || ext === 'docx') return 'word';
  return 'other';
};

const KIND_ICONS = { pdf: LuFileText, word: LuFileText, other: LuFile };

/**
 * Read-only list of documents assigned to the user (Policies, Job
 * Descriptions). A document opens in a viewer (PDF directly, Word through
 * Office's viewer); every row and the viewer offer a download.
 */
const AssignedDocumentList = ({ kind, documents, loading, error }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.files.${key}`, vars);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(null);

  const term = searchTerm.trim().toLocaleLowerCase('tr');
  const nameOf = (docItem) => docItem.name || label(`untitled.${kind}`);
  const filtered = documents.filter((d) => nameOf(d).toLocaleLowerCase('tr').includes(term)
    || (d.subtitle || '').toLocaleLowerCase('tr').includes(term));

  const download = (docItem) => downloadFileAs(docItem.fileUrl, docItem.fileName || nameOf(docItem));
  const selectedKind = selected ? getKind(selected) : null;

  let list;
  if (loading) {
    list = <LoadingRows count={3} />;
  } else if (error) {
    list = <EmptyState compact icon={LuTriangleAlert} title={label('loadError')} />;
  } else if (filtered.length === 0) {
    list = (
      <EmptyState
        compact
        icon={term ? LuSearchX : LuFolderOpen}
        title={term ? label('noMatch', { term: searchTerm.trim() }) : label(`empty.${kind}`)}
      />
    );
  } else {
    list = (
      <ul className="tw-space-y-2">
        {filtered.map((docItem) => {
          const docKind = getKind(docItem);
          return (
            <DocumentRow
              key={docItem.id}
              icon={KIND_ICONS[docKind]}
              tone={KIND_TONES[docKind]}
              title={nameOf(docItem)}
              onClick={() => setSelected(docItem)}
              meta={(docItem.version || docItem.subtitle) ? (
                <>
                  {docItem.version && <span className="tw-inline">v{docItem.version}</span>}
                  {docItem.subtitle && <span className="tw-inline">{docItem.subtitle}</span>}
                </>
              ) : null}
              trailing={docItem.fileUrl ? (
                <button
                  type="button"
                  onClick={() => download(docItem)}
                  title={label('download')}
                  aria-label={label('downloadNamed', { name: nameOf(docItem) })}
                  className="tw-grid tw-w-11 tw-shrink-0 tw-place-items-center tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-text-slate-600 tw-transition-colors hover:tw-border-teal-400/40 hover:tw-text-teal-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-text-slate-300 dark:hover:tw-text-teal-300"
                >
                  <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
                </button>
              ) : null}
            />
          );
        })}
      </ul>
    );
  }

  return (
    <div className="tw-space-y-3">
      <SearchField value={searchTerm} onChange={setSearchTerm} placeholder={label(`search.${kind}`)} />
      {list}

      <Modal
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        size="xl"
        title={selected ? nameOf(selected) : ''}
        subtitle={label(`categories.${kind}.title`)}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
            <LuFileText aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setSelected(null)} className="max-sm:tw-hidden">{t('common.close')}</Button>
            {selected?.fileUrl && (
              <Button variant="primary" onClick={() => download(selected)} className="max-sm:tw-flex-1">
                <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
                {label('download')}
              </Button>
            )}
          </>
        )}
      >
        {selected && (
          <div className="tw-space-y-4">
            {(selected.version || selected.subtitle || selected.description) && (
              <div className="tw-space-y-1.5">
                <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  {selected.version && <span className="tw-inline-flex tw-items-center tw-gap-1"><LuTag aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />{label('version', { version: selected.version })}</span>}
                  {selected.subtitle && <span className="tw-inline">{selected.subtitle}</span>}
                </p>
                {selected.description && <p className="tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{selected.description}</p>}
              </div>
            )}
            {!selected.fileUrl && <EmptyState compact icon={LuFile} title={label('noFile')} />}
            {selected.fileUrl && selectedKind === 'other' && <EmptyState compact icon={LuFile} title={label('cannotPreview')} />}
            {selected.fileUrl && selectedKind !== 'other' && (
              <iframe
                src={selectedKind === 'pdf'
                  ? `${selected.fileUrl}#navpanes=0`
                  : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(selected.fileUrl)}`}
                title={nameOf(selected)}
                className="tw-block tw-h-[min(70vh,720px)] tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white dark:tw-border-white/10"
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

AssignedDocumentList.propTypes = {
  kind: PropTypes.oneOf(['policies', 'jobDescriptions']).isRequired,
  documents: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default AssignedDocumentList;
